import { Suspense, lazy, useEffect } from 'react'
import styles from './App.module.css'
import { DebugConsole } from './debug/DebugConsole'
import { UpdateBanner } from './pwa/UpdateBanner'
import { matchRoute, paths, useRoute, type Route } from './router'
import { Editor } from './screens/Editor'
import { NewSong } from './screens/NewSong'
import { Play } from './screens/Play'
import { Repertoire } from './screens/Repertoire'
import { Settings } from './screens/Settings'
import { Timing } from './screens/Timing'
import { StorageGate } from './StorageGate'
import { t } from './strings'
import { saveRoute } from '../platform/session'

// Las pruebas de la entrega 0 no se necesitan para tocar: van en su propio chunk.
const ClockSpike = lazy(() =>
  import('./spikes/ClockSpike').then((module) => ({ default: module.ClockSpike })),
)
const DeviceSpike = lazy(() =>
  import('./spikes/DeviceSpike').then((module) => ({ default: module.DeviceSpike })),
)

function Screen({ route }: { route: Route }) {
  switch (route.name) {
    case 'new':
      return <NewSong />
    case 'play':
      return <Play arrangementId={route.arrangementId} />
    case 'edit':
      return <Editor arrangementId={route.arrangementId} />
    case 'timing':
      return <Timing arrangementId={route.arrangementId} />
    case 'settings':
      return <Settings />
    case 'clockSpike':
      return <ClockSpike />
    case 'deviceSpike':
      return <DeviceSpike />
    default:
      return <Repertoire />
  }
}

export function App() {
  const path = useRoute()
  const route = matchRoute(path)
  // Tocar ocupa toda la pantalla: sin cabecera ni márgenes, como un atril.
  const immersive = route.name === 'play'

  useEffect(() => {
    window.__atrilMounted = true
  }, [])

  useEffect(() => {
    saveRoute(path)
  }, [path])

  const links: { path: string; label: string; active: boolean }[] = [
    {
      path: paths.repertoire,
      label: t.nav.repertoire,
      active: ['repertoire', 'new', 'edit', 'timing'].includes(route.name),
    },
    { path: paths.settings, label: t.nav.settings, active: route.name === 'settings' },
  ]

  return (
    <div className={immersive ? styles.shellImmersive : styles.shell}>
      {!immersive && (
        <header className={styles.header}>
          <a className={styles.brand} href={`#${paths.repertoire}`}>
            {t.app.name}
          </a>
          <nav className={styles.nav}>
            {links.map((item) => (
              <a
                key={item.path}
                href={`#${item.path}`}
                className={item.active ? styles.navLinkActive : styles.navLink}
              >
                {item.label}
              </a>
            ))}
          </nav>
        </header>
      )}
      <main>
        <StorageGate>
          <Suspense fallback={null}>
            <Screen route={route} />
          </Suspense>
        </StorageGate>
      </main>
      <UpdateBanner />
      <DebugConsole />
    </div>
  )
}
